import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import UptimeDashboardLiveIndicator from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardLiveIndicator"
import type { DashboardOverallStatus } from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.types"

export type UptimeDashboardHeaderProps = ComponentProps<"header"> & {
  status: DashboardOverallStatus
  loading: boolean
  lastUpdatedAt: Date | null
  onExit(): void
}

// Top strip of the wall-display dashboard: brand dot + headline
// status copy on the left, live "Updated 4 s ago" pip + Exit button
// on the right. The brand area's font is tuned to be readable from
// across the room; the meta side is intentionally compact so the
// headline always wins the operator's eye.
function UptimeDashboardHeader(props: UptimeDashboardHeaderProps) {
  const { status, loading, lastUpdatedAt, onExit, className, ...headerProps } =
    props

  return (
    <header
      className={cn("uptime-dashboard-header", className)}
      {...headerProps}
    >
      <div className="uptime-dashboard-brand">
        <Dot
          variant={status.dotVariant}
          className="uptime-dashboard-brand-dot"
        />
        <div className="uptime-dashboard-brand-text">
          <div className="uptime-dashboard-brand-eyebrow">
            Placepoint · Service health
          </div>
          <div className="uptime-dashboard-brand-headline">
            {status.headline}
          </div>
        </div>
      </div>

      <div className="uptime-dashboard-header-meta">
        <UptimeDashboardLiveIndicator
          loading={loading}
          lastUpdatedAt={lastUpdatedAt}
        />
        <button
          type="button"
          className="uptime-dashboard-exit"
          onClick={onExit}
          aria-label="Exit dashboard mode"
        >
          <Icon src="cross" />
          Exit
        </button>
      </div>
    </header>
  )
}

export default UptimeDashboardHeader
